import React, { useCallback, useEffect, useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import SectionEditModal from '../profile/SectionEditModal.jsx';
import { uploadBrandAsset } from '../../lib/uploadBrandAsset.js';
import {
  getWorkExperiencesForDisplay,
  pickPrimaryWorkExperience,
  sortWorkExperiencesReverseChronological,
} from '../../lib/brandWorkExperience';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = currentYear; y >= 1980; y -= 1) YEAR_OPTIONS.push(y);

function newEntry() {
  return {
    id: `we-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    companyName: '',
    roleTitle: '',
    logoUrl: '',
    startMonth: 1,
    startYear: currentYear,
    endMonth: 12,
    endYear: currentYear,
    current: true,
  };
}

function hydrateEntries(brandProfile) {
  const list = getWorkExperiencesForDisplay(brandProfile);
  if (list.length === 0) return [newEntry()];
  return list.map((e) => ({
    id: e.id,
    companyName: e.companyName,
    roleTitle: e.roleTitle,
    logoUrl: e.logoUrl || '',
    startMonth: e.startMonth,
    startYear: e.startYear,
    endMonth: e.endMonth ?? 12,
    endYear: e.endYear ?? currentYear,
    current: !!e.current,
  }));
}

function startKey(e) {
  return (e.startYear || 0) * 12 + (e.startMonth || 0);
}

function endKey(e) {
  if (e.current) return Number.MAX_SAFE_INTEGER;
  return (e.endYear || 0) * 12 + (e.endMonth || 0);
}

/**
 * Modal: edit ordered work experience list + company logos. Syncs primary row to company.* for legacy consumers.
 */
function BrandWorkExperienceModal({ isOpen, onClose, userId, brandProfile, onSaved }) {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [errors, setErrors] = useState({});

  const hydrate = useCallback(() => {
    setEntries(hydrateEntries(brandProfile));
    setErrors({});
  }, [brandProfile]);

  useEffect(() => {
    if (isOpen) hydrate();
  }, [isOpen, hydrate]);

  const updateEntry = (id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id) => {
    setEntries((prev) => (prev.length <= 1 ? prev : prev.filter((e) => e.id !== id)));
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, newEntry()]);
  };

  const onLogoFile = async (entryId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors({ _general: 'Logo must be PNG, JPEG, or WebP.' });
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrors({ _general: 'Logo must be 2MB or less.' });
      return;
    }
    setErrors({});
    setUploadingId(entryId);
    try {
      const url = await uploadBrandAsset(file, userId, 'company-logo');
      updateEntry(entryId, { logoUrl: url });
    } catch (err) {
      setErrors({ _general: err.message || 'Logo upload failed.' });
    } finally {
      setUploadingId(null);
    }
  };

  const validate = () => {
    const next = {};
    if (!entries.length) next._general = 'Add at least one work experience.';
    entries.forEach((e, i) => {
      const prefix = `Row ${i + 1}: `;
      if (!e.companyName.trim()) next[`e${e.id}_company`] = `${prefix}Company name is required.`;
      if (!e.roleTitle.trim()) next[`e${e.id}_role`] = `${prefix}Role is required.`;
      if (!e.startMonth || !e.startYear) next[`e${e.id}_start`] = `${prefix}Start date is required.`;
      if (!e.current) {
        if (!e.endYear) next[`e${e.id}_end`] = `${prefix}End year is required when not current.`;
        else if (endKey(e) < startKey(e)) next[`e${e.id}_range`] = `${prefix}End date must be after start date.`;
      }
    });
    const currentCount = entries.filter((e) => e.current).length;
    if (currentCount > 1) {
      next._general = 'Only one role can be marked as current. Uncheck "I currently work here" on older roles.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!userId || !validate()) return;
    if (uploadingId) return;

    setSaving(true);
    setErrors({});
    try {
      const normalized = entries.map((e, idx) => ({
        id: e.id === 'legacy' ? `we-${Date.now()}-${idx}` : e.id,
        companyName: e.companyName.trim(),
        roleTitle: e.roleTitle.trim(),
        ...(e.logoUrl ? { logoUrl: e.logoUrl.trim() } : {}),
        startMonth: Number(e.startMonth),
        startYear: Number(e.startYear),
        current: !!e.current,
        ...(!e.current
          ? {
              endMonth: Number(e.endMonth) || 12,
              endYear: Number(e.endYear),
            }
          : {}),
      }));

      const sorted = sortWorkExperiencesReverseChronological(normalized);
      const primary = pickPrimaryWorkExperience(sorted);
      const prevCompany = brandProfile?.company || {};
      const { logoUrl: _prevLogo, ...prevCompanyRest } = prevCompany;

      const nextCompany = primary
        ? {
            ...prevCompanyRest,
            name: primary.companyName,
            roleTitle: primary.roleTitle,
            ...(primary.logoUrl ? { logoUrl: primary.logoUrl } : {}),
            timeAtCompany: {
              startMonth: primary.startMonth,
              startYear: primary.startYear,
            },
          }
        : { ...prevCompanyRest };

      const ref = doc(db, 'brandProfiles', userId);
      await setDoc(
        ref,
        {
          workExperiences: sorted,
          company: nextCompany,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      const merged = {
        ...brandProfile,
        workExperiences: sorted,
        company: nextCompany,
      };
      onSaved(merged);
      onClose();
    } catch (err) {
      console.error('[BrandWorkExperienceModal]', err);
      setErrors({ _general: err.message || 'Could not save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const rowError = (id) =>
    errors[`e${id}_company`] ||
    errors[`e${id}_role`] ||
    errors[`e${id}_start`] ||
    errors[`e${id}_end`] ||
    errors[`e${id}_range`];

  return (
    <SectionEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Work experience"
      subtitle="Add roles in reverse chronological order (most recent first). The current role syncs to your profile header."
      onSave={handleSave}
      isSaving={saving || !!uploadingId}
      errors={errors}
      saveLabel="Save experiences"
      disableSave={!!uploadingId}
    >
      <div className="space-y-6">
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Experience {index + 1}
              </span>
              {entries.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeEntry(entry.id)}
                  disabled={saving}
                  className="text-xs font-medium text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Company name</label>
              <input
                type="text"
                value={entry.companyName}
                onChange={(e) => updateEntry(entry.id, { companyName: e.target.value })}
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                placeholder="Company or organization"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Role / title</label>
              <input
                type="text"
                value={entry.roleTitle}
                onChange={(e) => updateEntry(entry.id, { roleTitle: e.target.value })}
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                placeholder="e.g. CEO, Marketing Lead"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Company logo</label>
              <div className="mt-2 flex items-center gap-3">
                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {entry.logoUrl ? (
                    <img src={entry.logoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">Logo</div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(ev) => onLogoFile(entry.id, ev)}
                  disabled={saving || uploadingId === entry.id}
                  className="block w-full text-sm text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-100 disabled:opacity-50"
                />
              </div>
              {uploadingId === entry.id && <p className="mt-1 text-xs text-slate-500">Uploading…</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">Start month</label>
                <select
                  value={entry.startMonth}
                  onChange={(e) => updateEntry(entry.id, { startMonth: Number(e.target.value) })}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm text-slate-900"
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Start year</label>
                <select
                  value={entry.startYear}
                  onChange={(e) => updateEntry(entry.id, { startYear: Number(e.target.value) })}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm text-slate-900"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={entry.current}
                onChange={(e) => updateEntry(entry.id, { current: e.target.checked })}
                className="rounded border-slate-300 text-[#1B9DA2] focus:ring-[#1B9DA2]"
              />
              <span className="text-sm text-slate-700">I currently work here</span>
            </label>

            {!entry.current && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600">End month</label>
                  <select
                    value={entry.endMonth}
                    onChange={(e) => updateEntry(entry.id, { endMonth: Number(e.target.value) })}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm text-slate-900"
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">End year</label>
                  <select
                    value={entry.endYear}
                    onChange={(e) => updateEntry(entry.id, { endYear: Number(e.target.value) })}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm text-slate-900"
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {rowError(entry.id) && <p className="text-xs text-red-600">{rowError(entry.id)}</p>}
          </div>
        ))}

        <button
          type="button"
          onClick={addEntry}
          disabled={saving}
          className="w-full rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-slate-600 hover:border-[#1B9DA2] hover:text-[#1B9DA2] transition-colors"
        >
          + Add work experience
        </button>
      </div>
    </SectionEditModal>
  );
}

export default BrandWorkExperienceModal;
